import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Building, CheckCircle2, XCircle, Clock, Loader2, ChevronDown, ChevronUp } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminPractices = () => {
  const [practices, setPractices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    fetchPractices();
  }, []);

  const fetchPractices = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/admin/practices`);
      setPractices(res.data.practices || []);
    } catch (err) {
      console.error('Failed to fetch practices:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleAction = async (practiceId, action) => {
    try {
      await axios.put(`${API_URL}/api/admin/practices/${practiceId}`, { action });
      toast.success(`Practice ${action}d`);
      fetchPractices();
    } catch (error) {
      toast.error(error.response?.data?.detail || `Failed to ${action}`);
    }
  };

  const pending = practices.filter(p => p.status === 'pending');
  const approved = practices.filter(p => p.status === 'approved');

  const statusIcon = (status) => {
    if (status === 'approved') return <CheckCircle2 className="h-3.5 w-3.5 text-emerald-400" />;
    if (status === 'rejected') return <XCircle className="h-3.5 w-3.5 text-red-400" />;
    return <Clock className="h-3.5 w-3.5 text-amber-400" />;
  };

  return (
    <Card className="bg-slate-800 border-slate-700">
      <CardHeader className="pb-3 cursor-pointer" onClick={() => setExpanded(!expanded)}>
        <div className="flex items-center justify-between">
          <CardTitle className="text-white text-base flex items-center gap-2">
            <Building className="h-4 w-4 text-sky-400" />
            Accountant Practices ({practices.length})
            {pending.length > 0 && (
              <span className="text-[10px] px-1.5 py-0.5 bg-amber-500/15 text-amber-400 rounded-full">{pending.length} pending</span>
            )}
          </CardTitle>
          {expanded ? <ChevronUp className="h-4 w-4 text-slate-400" /> : <ChevronDown className="h-4 w-4 text-slate-500" />}
        </div>
      </CardHeader>
      {expanded && (
        <CardContent className="pt-0">
          {loading ? (
            <div className="flex justify-center py-4"><Loader2 className="h-5 w-5 animate-spin text-sky-400" /></div>
          ) : practices.length === 0 ? (
            <p className="text-sm text-slate-500 text-center py-4">No practices registered yet.</p>
          ) : (
            <div className="space-y-2">
              {practices.map(p => (
                <div key={p.id} className="p-3 bg-slate-700/30 rounded-lg border border-slate-600" data-testid={`practice-${p.id}`}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        {statusIcon(p.status)}
                        <p className="text-sm font-medium text-white">{p.firm_name}</p>
                      </div>
                      <div className="grid grid-cols-2 gap-x-4 gap-y-1 mt-2 text-xs text-slate-400">
                        <span>HMRC ARN: <span className="text-white">{p.hmrc_arn}</span></span>
                        <span>Body: <span className="text-white">{p.professional_body}</span></span>
                        <span>Address: <span className="text-slate-300">{p.branch_address}</span></span>
                        <span>Phone: <span className="text-slate-300">{p.phone}</span></span>
                        {p.admin_user && (
                          <span className="col-span-2">Contact: <span className="text-white">{p.admin_user.name}</span> ({p.admin_user.email})</span>
                        )}
                      </div>
                    </div>
                    {p.status === 'pending' && (
                      <div className="flex gap-1 shrink-0">
                        <Button size="sm" onClick={() => handleAction(p.id, 'approve')}
                          className="bg-emerald-600 hover:bg-emerald-700 h-7 text-xs" data-testid={`approve-${p.id}`}>
                          Approve
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => handleAction(p.id, 'reject')}
                          className="text-red-400 hover:bg-red-400/10 h-7 text-xs" data-testid={`reject-${p.id}`}>
                          Reject
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      )}
    </Card>
  );
};

export default AdminPractices;
